import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { ConnectivityService } from './connectivity.service';

/**
 * How long the app bootstrap waits for the platform to report the connectivity status before
 * starting with the optimistic one.
 */
export const CONNECTIVITY_START_TIMEOUT_MS = 3_000;

/**
 * Starts the connectivity status, giving up after `timeoutMs`.
 *
 * Used by the app bootstrap so a plugin that does not answer cannot block the app: after the
 * timeout the app starts with the optimistic status and the listeners and the polling fallback
 * correct it.
 */
export function startConnectivityWithTimeout(connectivity: ConnectivityService, timeoutMs: number): Promise<void> {
  const start = connectivity.start().catch(() => undefined);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));

  return Promise.race([start, timeout]);
}

/**
 * @description
 * Makes the connectivity status known before the app starts doing anything else, requests included.
 *
 * Without it the status is only read once the root component is created, so the requests started
 * before that moment (translations, error handling) slip through the offline interceptor. It must
 * run as an app initializer, before the first component and before the initial navigation.
 */
export function provideConnectivityBootstrap(timeoutMs = CONNECTIVITY_START_TIMEOUT_MS): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const connectivity = inject(ConnectivityService);

      return startConnectivityWithTimeout(connectivity, timeoutMs);
    }),
  ]);
}
